import { FC, useEffect } from 'react';
import { createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { SchemaFormFields } from '@mews-commander/form';
import { useCommanderSelector } from '@mews-commander/utils';
import { TextKey } from '@mews-commander/enums';
import { FormLayout } from '../../FormLayout';
import { ReleaseFormSchema, ReleaseFormValues } from './types';
import { useUpdateReleaseMutation } from './useUpdateReleaseMutation';
import { getReleaseFormInitialValues } from './selectors';

interface ReleaseFormProps {
    reservationIds: string[];
    scope: string;
    onSuccess: () => void;
}

export const ReleaseForm: FC<ReleaseFormProps> = ({ reservationIds, scope, onSuccess }) => {
    const defaultValues = useCommanderSelector((state) =>
        getReleaseFormInitialValues(state, { scope })
    ) as ReleaseFormValues;
    const mutation = useUpdateReleaseMutation(reservationIds);

    const onSubmit = (values: ReleaseFormValues) => {
        mutation.mutate(values, { onSuccess });
    };

    const { renderFormContent, setValue } = useFormControls<ReleaseFormValues>({
        defaultValues,
        dataTestForm: 'release-form',
        onSubmit,
        resolver: createZodFormResolver(ReleaseFormSchema),
    });

    useEffect(() => {
        setValue('releasedUtc', defaultValues.releasedUtc);
    }, [defaultValues.releasedUtc, setValue]);

    return renderFormContent(
        <FormLayout loading={mutation.isPending} vertical={false}>
            <SchemaFormFields
                schema={ReleaseFormSchema}
                formDefinition={{
                    fields: {
                        releasedUtc: {
                            label: lt(TextKey.ReleaseOn),
                            field: {
                                type: 'dateTime',
                            },
                            required: true,
                        },
                    },
                }}
            />
        </FormLayout>
    );
};
